import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";

export function EggCatch({ onScore }) {
  const [basket, setBasket] = useState(50);
  const [eggs, setEggs] = useState([]);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [running, setRunning] = useState(false);
  const [over, setOver] = useState(false);
  const raf = useRef(null);
  const idRef = useRef(0);
  const basketRef = useRef(50);
  const reported = useRef(false);

  const start = () => { setBasket(50); basketRef.current = 50; setEggs([]); setScore(0); setLives(3); setRunning(true); setOver(false); reported.current = false; };

  useEffect(() => {
    if (!running) return;
    let frame = 0; let local = []; let sc = 0; let lv = 3;
    const loop = () => {
      frame++;
      if (frame % 45 === 0) local.push({ id: idRef.current++, x: 8 + Math.random() * 84, y: 0, bomb: Math.random() < 0.2 });
      local = local.map((e) => ({ ...e, y: e.y + 1.6 }));
      const kept = [];
      for (const e of local) {
        if (e.y >= 88 && e.y <= 100) {
          if (Math.abs(e.x - basketRef.current) < 12) { if (e.bomb) { lv--; setLives(lv); } else { sc++; setScore(sc); } continue; }
        }
        if (e.y > 100) { if (!e.bomb) { /* missed egg ok */ } continue; }
        kept.push(e);
      }
      local = kept;
      setEggs([...local]);
      if (lv <= 0) { setRunning(false); setOver(true); }
      else raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf.current);
  }, [running]);

  const move = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.touches ? e.touches[0].clientX : e.clientX) - rect.left) / rect.width * 100;
    basketRef.current = Math.max(8, Math.min(92, x)); setBasket(basketRef.current);
  };

  useEffect(() => { if (over && !reported.current) { reported.current = true; onScore?.(score * 40); } }, [over, score, onScore]);

  return (
    <div className="flex flex-col items-center" data-testid="egg-catch">
      <div className="flex gap-6 mb-3 font-mono">
        <span className="text-neon-cyan text-lg" data-testid="egg-score">CAUGHT: {score}</span>
        <span className="text-neon-pink text-lg">❤️ {lives}</span>
      </div>
      <div onMouseMove={move} onTouchMove={move} className="relative bg-zinc-950 border-2 border-zinc-700 rounded-xl overflow-hidden" style={{ width: "min(92vw, 380px)", height: 340, touchAction: "none" }}>
        {eggs.map((e) => (
          <div key={e.id} className="absolute text-2xl" style={{ left: `${e.x}%`, top: `${e.y}%`, transform: "translate(-50%,-50%)" }}>{e.bomb ? "💣" : "🥚"}</div>
        ))}
        <div className="absolute text-3xl" style={{ left: `${basket}%`, bottom: 4, transform: "translateX(-50%)" }}>🧺</div>
        {!running && (
          <div className="absolute inset-0 bg-black/85 flex flex-col items-center justify-center gap-3">
            {over && <p className="font-display font-black text-2xl text-neon-lime uppercase" data-testid="egg-over">Caught {score} eggs!</p>}
            <Button onClick={start} data-testid="egg-start" className="bg-neon-cyan text-black rounded-none font-bold uppercase">
              <Play className="w-4 h-4 mr-1 fill-black" /> {over ? "Play Again" : "Start"}
            </Button>
          </div>
        )}
      </div>
      <p className="text-zinc-500 text-xs font-mono mt-3">Move the basket to catch eggs — avoid the bombs!</p>
    </div>
  );
}
